import React, { useState, useEffect, useCallback } from 'react';
import { auditAPI } from '../../services/api';
import AdminSidebar from '../../components/AdminSidebar';
import { useToast } from '../../contexts/ToastContext';
import { Shield, RefreshCw, Search, ChevronDown, ChevronRight } from 'lucide-react';

const ACTION_COLOR = { create:'#10b981', update:'#3b82f6', patch:'#3b82f6', delete:'#ef4444', merge:'#8b5cf6' };

const AdminAuditLog = () => {
  const toast = useToast();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEntity, setFilterEntity] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await auditAPI.getAll({
        entity: filterEntity || undefined,
        action: filterAction || undefined,
        q: search || undefined,
        limit: 100
      });
      setLogs(res.data.logs || res.data || []);
    } catch {
      toast.error('Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [filterEntity, filterAction, search]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const fmtDate = (d) => new Date(d).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' });

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#0a0a0a', color: '#fafafa' }}>
      <AdminSidebar />
      <div style={{ flex: 1, padding: '32px 40px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 28, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
              <Shield size={28} color="#f97316" /> Audit Log
            </h1>
            <p style={{ color: '#a1a1aa', marginTop: 4 }}>Every admin write — who did what, when, before/after</p>
          </div>
          <button onClick={load} style={btnGhost}><RefreshCw size={16}/></button>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '1 1 240px' }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: '#71717a' }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search entity ID / admin email…"
              style={{ ...input, paddingLeft: 32 }} />
          </div>
          <select value={filterEntity} onChange={e => setFilterEntity(e.target.value)} style={{ ...input, maxWidth: 160 }}>
            <option value="">All entities</option>
            <option value="Customer">Customer</option>
            <option value="Lead">Lead</option>
            <option value="Order">Order</option>
            <option value="Quote">Quote</option>
            <option value="Invoice">Invoice</option>
            <option value="Task">Task</option>
            <option value="Opportunity">Opportunity</option>
          </select>
          <select value={filterAction} onChange={e => setFilterAction(e.target.value)} style={{ ...input, maxWidth: 140 }}>
            <option value="">All actions</option>
            <option value="create">Create</option>
            <option value="update">Update</option>
            <option value="delete">Delete</option>
          </select>
        </div>

        {loading ? <div style={{ color: '#a1a1aa' }}>Loading…</div> : logs.length === 0 ? (
          <div style={{ ...card, textAlign: 'center', padding: 40, color: '#71717a' }}>No audit entries.</div>
        ) : (
          <div style={card}>
            {logs.map((log, i) => {
              const key = log._id || i;
              const open = !!expanded[key];
              const color = ACTION_COLOR[(log.action || '').toLowerCase()] || '#a1a1aa';
              return (
                <div key={key} style={{ borderBottom: i < logs.length - 1 ? '1px solid #27272a' : 'none', padding: '12px 0' }}>
                  <div onClick={() => setExpanded(p => ({ ...p, [key]: !open }))} style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
                    {open ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
                    <span style={{ ...badge, background: color + '22', color }}>{(log.action || 'unknown').toUpperCase()}</span>
                    <span style={{ fontWeight: 500 }}>{log.entity}</span>
                    <code style={{ fontSize: 11, color: '#71717a' }}>{log.entityId}</code>
                    <span style={{ color: '#71717a', fontSize: 12, marginLeft: 'auto' }}>{log.adminId?.email || log.adminId || 'system'}</span>
                    <span style={{ color: '#71717a', fontSize: 12 }}>{fmtDate(log.occurredAt || log.createdAt)}</span>
                  </div>
                  {open && (
                    <div style={{ marginTop: 10, marginLeft: 26, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div>
                        <div style={lbl}>Before</div>
                        <pre style={pre}>{JSON.stringify(log.before || {}, null, 2)}</pre>
                      </div>
                      <div>
                        <div style={lbl}>After</div>
                        <pre style={pre}>{JSON.stringify(log.after || {}, null, 2)}</pre>
                      </div>
                      {log.ip && <div style={{ gridColumn: '1 / -1', fontSize: 11, color: '#71717a' }}>IP: {log.ip}</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const card = { background: '#111', border: '1px solid #27272a', borderRadius: 8, padding: 16 };
const btnGhost = { background: '#18181b', color: '#a1a1aa', border: '1px solid #27272a', padding: '8px 12px', borderRadius: 6, cursor: 'pointer' };
const input = { padding: '8px 10px', background: '#18181b', border: '1px solid #27272a', borderRadius: 6, color: '#fafafa', fontSize: 13, width: '100%' };
const badge = { padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, letterSpacing: '.5px' };
const lbl = { fontSize: 10, color: '#71717a', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 4 };
const pre = { background: '#0a0a0a', border: '1px solid #27272a', borderRadius: 4, padding: 10, fontSize: 11, color: '#a1a1aa', maxHeight: 200, overflow: 'auto', margin: 0 };

export default AdminAuditLog;
